import { Injectable, QueryList } from '@angular/core';
import * as XLSX from 'xlsx';
import { ViewTableComponent, UtilService } from 'beyond-util';
import { ReportHeaderData } from '../models/ReportHeaderData';
import { IzvestajSettings } from '../pages/izvestaji/prihodi-po-emisijama/izvestaj-settings.service';
import { FontService } from '../fonts/font.service';
declare var jsPDF: any;

@Injectable()
export class ExportService {
  constructor(
    private izvestajSettings: IzvestajSettings,
    private fontService: FontService
  ) {}

  getObjectKey(object) {
    const names = [];
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        names.push(key);
      }
    }

    return names;
  }

  getObjectValue(object) {
    const values = [];
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        values.push(object[key]);
      }
    }

    return values;
  }

  remapReportHeader(report) {
    const settings = this.izvestajSettings.prihodiPoEmisijamaHeaderSettings
      .allFields;
    const exportHeader: ReportHeaderData[] = [];

    for (let i = 0; i < settings.length; i++) {
      if (settings[i].fieldKey === this.getObjectKey(report)[i]) {
        exportHeader.push(
          new ReportHeaderData(
            settings[i].label,
            this.getObjectValue(report)[i]
          )
        );
      }
    }

    return exportHeader;
  }

  exportToExcel(tables: QueryList<ViewTableComponent>, report, settingsTitle) {
    tables.forEach(child => {
      const header = this.remapReportHeader(report);

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(header, {
        skipHeader: true,
      });

      /* add table data to worksheet */
      const exportTable = child.getTotalTableData();
      XLSX.utils.sheet_add_json(ws, exportTable, { origin: 'A11' });

      /* generate workbook and add the worksheet */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      /* save to file */
      const title =
        settingsTitle + UtilService.formatDate(new Date().toISOString());
      XLSX.writeFile(wb, UtilService.replaceCharacters(title) + '.xls');
    });
  }

  // setujete header footer i kolone pre nego sto printas inace printa praznu tabelu
  setupPage(pageTitle = 'Title', pageHeader, tableHeader, tableBody) {
    const doc = new jsPDF({
      unit: 'pt',
      orientation: 'landscape',
      format: 'a4',
      lineHeight: 1.3,
    });

    this.fontService.AddFontArimo(doc);

    const headerStartingMargin = 160; // margin top gde pocinje header
    const startingWidth = 45; // margin left za celu stranu
    const columnRowCount = 4; // redova po koloni u headeru

    if (tableBody.length <= 15) {
      this.printPageOneOfDocument(
        doc,
        pageTitle,
        pageHeader,
        tableBody,
        tableHeader,
        headerStartingMargin,
        45,
        4,
        9
      );
    } else {
      const firstPartOfTheTableBody = tableBody.slice(0, 15);
      const lastIndex = tableBody.lastIndexOf();
      const secondPartOfTableBody = tableBody.slice(15, lastIndex);

      this.printPageOneOfDocument(
        doc,
        pageTitle,
        pageHeader,
        firstPartOfTheTableBody,
        tableHeader,
        headerStartingMargin,
        45,
        4,
        9
      );
      doc.addPage();
      doc.setPage(2);
      this.createTable(doc, tableHeader, secondPartOfTableBody, 20);
    }

    const pageCount = doc.internal.getNumberOfPages();
    const footerStartingMargin = doc.previousAutoTable.finalY + 20;

    this.initPaging(pageCount, doc);

    this.printContentInColumnsWithRows(
      footerStartingMargin,
      startingWidth,
      [{ label: 'Ukupno trajanje', value: '124s' }],
      columnRowCount,
      doc
    );

    doc.autoPrint();
    doc.output('dataurlnewwindow');
  }

  private initPaging(pageCount: any, doc: any) {
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const str = 'Stranica ' + i + ' od ' + pageCount;
      const width = doc.internal.pageSize.width - 100;
      doc.text(str, width, doc.internal.pageSize.height - 20);
    }
  }

  createTable(doc, tableHeader, tableBody, tableMarginTop) {
    doc.autoTable(tableHeader, tableBody, {
      margin: {
        top: tableMarginTop,
        left: 45,
      },
      tableLineColor: [189, 195, 199],
      tableLineWidth: 0.75,
      theme: 'grid',
      styles: {
        font: 'arimo',
        lineColor: [44, 62, 80],
        lineWidth: 0.55,
        halign: 'center',
        fontSize: 9,
      },
      headStyles: {
        fillColor: [60, 60, 60],
        fontSize: 9,
      },
      bodyStyles: {
        fillColor: [216, 216, 216],
        textColor: 50,
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
    });
  }

  printPageOneOfDocument(
    doc,
    pageTitle,
    pageHeader,
    firstPartOfTheTableBody,
    tableHeader,
    headerStartingMargin,
    startingWidth,
    columnRowCount,
    fontSize
  ) {
    doc.setPage(1);
    this.printTitle(doc, pageTitle);

    this.printContentInColumnsWithRows(
      headerStartingMargin,
      startingWidth,
      pageHeader,
      columnRowCount,
      doc
    );

    const tableMarginTop = this.calculateTableStartingHeight(
      headerStartingMargin,
      columnRowCount,
      fontSize
    );

    this.createTable(doc, tableHeader, firstPartOfTheTableBody, tableMarginTop);
  }
  printContentInColumnsWithRows(
    startingMargin,
    startingWidth,
    content,
    rows,
    doc
  ) {
    // samo po jedan niz labela i vrednosti
    const labelsNotSorted = this.getLabelsArray(content);
    const valuesNotSorted = this.getValuesArray(content);

    // rasporedjen niz u kolone po 3 reda
    const labelsSorted = this.createColumns(
      labelsNotSorted,
      labelsNotSorted.length,
      rows
    );

    const valuesSorted = this.createColumns(
      valuesNotSorted,
      valuesNotSorted.length,
      rows
    );

    for (let i = 0; i < labelsSorted.length; i++) {
      doc.setFont('arimo', 'bold');
      doc.setTextColor('#5F021F');
      const label = this.initTextFromArray(labelsSorted[i], 'label');
      doc.text(label.text, startingWidth, startingMargin, 'left');

      const value = this.initTextFromArray(valuesSorted[i], 'value');

      doc.setFont('arimo', 'normal');
      doc.setTextColor('#000000');

      doc.text(
        value.text,
        startingWidth + doc.getTextWidth(label.element) + 15,
        startingMargin,
        'left'
      );

      startingWidth = startingWidth + 170;
    }
  }

  calculateTableStartingHeight(headerStartHeight, columnRowCount, fontSize) {
    return Math.round((columnRowCount + 1) * fontSize + headerStartHeight);
  }

  initTextFromArray(array, type: any) {
    let text = '';
    let lines = 0;
    let maxWidth = 0;
    let elementMaxWidth = '';
    let count = 0;
    if (type === 'label') {
      array.forEach(element => {
        text += `${element}:\n`;

        if (maxWidth < element.length) {
          maxWidth = element.length;
          elementMaxWidth = element;
        }

        lines++;
        count++;
      });
    }

    if (type === 'value') {
      array.forEach(element => {
        text += `${element}\n`;

        if (maxWidth < element.length) {
          maxWidth = element.length;
          elementMaxWidth = element;
        }

        lines++;
      });
    }

    return {
      text: text,
      lines: lines,
      element: elementMaxWidth,
    };
  }

  /// Vraca broj redova u headeru najvise 3 po koloni
  getCoulumnCount(allCount, rowCount) {
    if (allCount % rowCount === 0) {
      return allCount / rowCount;
    }
    if (allCount % rowCount !== 0) {
      if (allCount / rowCount < 1) {
        return 1;
      } else {
        const rest = Math.trunc(allCount / rowCount);

        return rest + 1;
      }
    }
  }

  createColumns(columnToRearange, count, rows) {
    let ii = 0;
    const columns = this.getCoulumnCount(count, rows);
    const allColumns = new Array(columns);

    for (let i = 0; i < columns; i++) {
      allColumns[i] = new Array(rows);
      for (let j = 0; j < rows; j++) {
        if (ii < count) {
          allColumns[i][j] = columnToRearange[ii];
          ii++;
        }
      }
    }

    return allColumns;
  }

  // vraca niz labela
  getLabelsArray(array) {
    const labels = [];

    array.forEach(e => {
      labels.push(e.label);
    });

    return labels;
  }

  // vraca niz vrednosti
  getValuesArray(array) {
    const values = [];
    array.forEach(e => {
      values.push(e.value);
    });

    return values;
  }

  getTableRowsFromServiceData(array) {
    const rows = [];
    array.forEach(e => {
      rows.push(this.getObjectValue(e));
    });

    return rows;
  }

  createPageHeaderFromSettingsAndServiceData(allFields, serviceData) {
    const pageHeader = [];

    const pageHeaderLabels = this.getLabelsArray(allFields);
    const pageHeaderValues = this.getObjectValue(serviceData);

    for (let i = 0; i < pageHeaderLabels.length; i++) {
      pageHeader.push({
        label: pageHeaderLabels[i],
        value: pageHeaderValues[i],
      });
    }

    return pageHeader;
  }

  printTitle(doc, pageTitle) {
    doc.setFontSize(20);
    doc.setFont('arimo', 'bold');
    doc.text(45, 100, pageTitle);
    doc.setFontSize(9);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/export.service.ts